<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { computed, ref } from 'vue';
// NOTE: Required both imports to make useKeenSlider library work
// Check https://keen-slider.io/docs for official documentation
import { useKeenSlider } from 'keen-slider/vue';

import { assertValue } from '@libs/utils';

import { AppFadeTransition } from '../../transitions';
import { KeyboardKeyCode } from '../../../models';
import AppGalleryDots from '../AppGalleryDots.vue';
import AppImg from '../../appImg/AppImg.vue';
import AppNumberOfSlides from '../AppNumberOfSlides.vue';
import { getIsSmallDevice } from '../../../helpers/isSmallDevice';
import AppIconButton from '../../appIconButton/AppIconButton.vue';

export interface AppFullScreenSingleImageGalleryProps {
  imageUrls: string[];
  initialSlide: number;
  closeGalleryAriaLabel: string;
  leftArrowAriaLabel: string;
  rightArrowAriaLabel: string;
  errorImageHandler?: (src: string) => void;
}

const props = withDefaults(
  defineProps<AppFullScreenSingleImageGalleryProps>(),
  {
    initialSlide: 0,
  }
);

interface AppFullScreenSingleImageGalleryEmits {
  (event: 'close'): void;
}

const emit = defineEmits<AppFullScreenSingleImageGalleryEmits>();

const galleryDotsRef = ref<HTMLDivElement | null>(null);
const galleryWrapperRef = ref<HTMLDivElement | null>(null);

const currentSlide = ref<number>(props.initialSlide);
const isSliderLoaded = ref<boolean>(false);

const numberOfSlides = computed<number>(() => props.imageUrls.length);
const shouldShowGalleryNavigation = computed(
  () => slider.value && isSliderLoaded.value && props.imageUrls.length > 1
);

onClickOutside(galleryWrapperRef, () => emit('close'), {
  ignore: [galleryDotsRef],
});

const isSmallDevice = computed(() => getIsSmallDevice());

// NOTE: Required core funtion to make useKeenSlider library work
const [container, slider] = useKeenSlider({
  initial: props.initialSlide,
  slideChanged(slider) {
    setCurrentSlide(slider.track.details.rel);
  },
  created() {
    isSliderLoaded.value = true;
  },
});

function setCurrentSlide(slide: number): void {
  currentSlide.value = slide;
}

function handleLeftClick(): void {
  assertValue(
    slider.value,
    'AppFullscreenSingleImageGallery.vue > handleLeftClick > slider'
  );

  slider.value.prev();
}

function handleRightClick(): void {
  assertValue(
    slider.value,
    'AppFullscreenSingleImageGallery.vue > handleRightClick > slider'
  );

  slider.value.next();
}

function handleDotClick(dotNumber: number): void {
  assertValue(
    slider.value,
    'AppFullscreenSingleImageGallery.vue > handleDotClick > slider'
  );

  slider.value.moveToIdx(dotNumber);
}

onKeyStroke(KeyboardKeyCode.Escape, () => emit('close'));
</script>

<template>
  <div class="app-full-screen-single-image-gallery">
    <div class="gallery-header">
      <AppNumberOfSlides
        class="number-of-slides"
        :current-slide="currentSlide"
        :number-of-slides="numberOfSlides"
        background="transparent-background"
      />
      <AppIconButton
        class="close-button"
        type="secondary"
        state="enabled"
        size="medium"
        :icon="{ name: 'close' }"
        :ariaLabel="props.closeGalleryAriaLabel"
        @click="emit('close')"
      />
    </div>
    <div ref="galleryWrapperRef" class="gallery-wrapper">
      <AppIconButton
        v-if="
          !isSmallDevice && shouldShowGalleryNavigation && currentSlide !== 0
        "
        class="arrow-left"
        state="enabled"
        size="medium"
        type="secondary"
        :icon="{ name: 'chevronLeft' }"
        :ariaLabel="props.leftArrowAriaLabel"
        @click="handleLeftClick"
      />
      <!-- NOTE: Required classes keen-slider and keen-slider__slide to make useKeenSlider library work -->

      <div ref="container" class="keen-slider">
        <div
          v-for="(image, index) in props.imageUrls"
          :key="index"
          class="keen-slider__slide"
        >
          <AppFadeTransition>
            <div class="image-container">
              <AppImg
                class="image"
                :src="image"
                alt="image"
                :track-time="false"
                @img-load-error="props.errorImageHandler"
              />
            </div>
          </AppFadeTransition>
        </div>
      </div>
      <AppIconButton
        v-if="
          !isSmallDevice &&
          shouldShowGalleryNavigation &&
          currentSlide !== numberOfSlides - 1
        "
        class="arrow-right"
        state="enabled"
        size="medium"
        type="secondary"
        :icon="{ name: 'chevronRight' }"
        :ariaLabel="props.rightArrowAriaLabel"
        @click="handleRightClick"
      />
    </div>
    <AppGalleryDots
      v-if="isSmallDevice && shouldShowGalleryNavigation"
      ref="galleryDotsRef"
      class="gallery-dots"
      :current-slide="currentSlide"
      :number-of-slides="numberOfSlides"
      @dot-clicked="handleDotClick"
    />
  </div>
</template>

<style scoped lang="scss">
@import url('keen-slider/keen-slider.css');
@import '../../../styles/media';

.app-full-screen-single-image-gallery {
  --max-width: 1200px;

  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;

  display: grid;
  grid-template-rows: 12.5vh 75vh 12.5vh;

  width: 100vw;
  height: 100vh;
  height: 100dvh;

  z-index: var(--panel-third-level-z-index);
  background-color: var(--color-overlay-dark);
  padding: 0 var(--spacing-16);

  .gallery-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    width: 100%;
    max-width: var(--max-width);

    margin: 0 auto;

    .number-of-slides {
      min-width: 0;
    }
    .close-button {
      box-shadow: var(--shadow);
      cursor: pointer;
    }
  }

  .gallery-wrapper {
    position: relative;

    width: 100%;
    max-width: var(--max-width);

    margin: 0 auto;
    border-radius: var(--border-radius-12);
    overflow: hidden;

    .arrow-left {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);

      z-index: 1;
      margin: var(--spacing-24);
    }
    .arrow-right {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);

      z-index: 1;
      margin: var(--spacing-24);
    }

    .keen-slider {
      display: flex;
      align-items: center;
      height: 100%;
      .keen-slider__slide {
        display: grid;
        align-items: center;

        .image-container {
          border-radius: var(--border-radius-12);
          overflow: hidden;

          .image {
            width: 100%;
            height: 100%;
            object-fit: cover;

            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            cursor: grab;
          }
        }
      }
    }
  }
  .gallery-dots {
    padding: 0 var(--spacing-24);
    bottom: var(--spacing-16);
  }
}
</style>
